import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useOrder } from '../context/OrderContext';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useBrand } from '../context/BrandContext';
import { submitQrOrder, db, paypalCreateOrder } from '../services/firebase';
import { getOrderRequestId, clearOrderRequestId } from '../utils/orderRequestId';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import AnalogTimePicker from '../components/AnalogTimePicker';
import OrderOTPModal from '../components/OrderOTPModal';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { loadDeliveryZone, quoteDelivery, DeliveryQuote } from '../services/deliveryZones';
import { currentBranchId } from '../config/branchContext';
import { loadBranchScheduleOnce, branchHasSchedule, computeStatus, preorderOptions, PreorderOption, BranchSchedule } from '../services/branchSchedule';

// Fallback minimum when the ZIP has no configured delivery zone. Admin-entered
// zones override this — the whole point of Phase 3 is per-ZIP rules.
const MIN_DELIVERY_AMOUNT = 15;

// German mobile prefixes (after +49 normalisation). Everything else = landline.
const GERMAN_MOBILE_PREFIXES = ['15', '16', '17'];

// PayPal is off until the live PayPal keys are in (24 Oct 2026). The functions
// still run in sandbox mode and paypalCreateOrder rejects OTP-session customers
// ("User must be authenticated"), so every real PayPal order so far reached the
// kitchen and then showed the customer an error. Turn back on only together
// with live keys and the auth fix in functions/paypalFunctions.js.
const PAYPAL_ENABLED = false;

/** Returns true if the phone is a German mobile (can receive SMS/WhatsApp OTP). */
function isMobilePhone(raw: string): boolean {
  const p = raw.replace(/[\s\-()]/g, '');
  // International non-DE numbers — assume they CAN receive SMS
  if (p.startsWith('+') && !p.startsWith('+49')) return true;
  if (p.startsWith('00') && !p.startsWith('0049')) return true;
  // German numbers
  const national = p.startsWith('+49') ? p.slice(3)
    : p.startsWith('0049') ? p.slice(4)
    : p.startsWith('0') ? p.slice(1)
    : p;
  return GERMAN_MOBILE_PREFIXES.some(pre => national.startsWith(pre));
}

interface CheckoutPageProps {
  /** When embedded in a modal (desktop flow), skip the page chrome + show close button. */
  inModal?: boolean;
  onClose?: () => void;
}

const CheckoutPage: React.FC<CheckoutPageProps> = ({ inModal = false, onClose }) => {
  const navigate = useNavigate();
  const { items, clearCart, total, itemCount } = useCart();
  const { mode, tableNumber, deliveryInfo, setDeliveryInfo, pickupInfo, setPickupInfo } = useOrder();
  const { user, profile } = useAuth();
  const { lang, setLang, t } = useLanguage();
  const { brand } = useBrand();

  const isGuest = !user;

  const [guestEmail, setGuestEmail] = useState('');
  const [timePickerOpen, setTimePickerOpen] = useState(false);

  // Auto-fill email from profile/session (was always empty before this fix)
  useEffect(() => {
    if (!guestEmail) {
      const profileEmail = profile?.email || user?.email || '';
      if (profileEmail) setGuestEmail(profileEmail);
    }
  }, [profile, user]); // eslint-disable-line

  // Delivery zone quote (live, based on current ZIP + cart subtotal)
  const [deliveryQuote, setDeliveryQuote] = useState<DeliveryQuote | null>(null);
  const [zoneChecking, setZoneChecking] = useState(false);

  // OTP
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [pendingOrderData, setPendingOrderData] = useState<any>(null);

  // "Placing your order..." overlay — shown between OTP close and confirmation
  // page navigation so the customer never sees the form flash in between.
  const [confirmingOrder, setConfirmingOrder] = useState(false);

  // Validation: tracks whether the user has attempted to submit at least once.
  // Required-field errors are only shown AFTER the first attempt so fresh visitors
  // don't see a wall of red immediately.
  const [attempted, setAttempted] = useState(false);

  // Auto-fill from profile
  React.useEffect(() => {
    if (profile) {
      if (isDelivery && !deliveryInfo.name) {
        setDeliveryInfo({
          name: profile.name || '',
          street: profile.street || '',
          postcode: profile.postcode || '',
          city: profile.city || '',
          phone: profile.phone || '',
          notes: '',
        });
      }
      if (isPickup && !pickupInfo.name) {
        setPickupInfo({
          name: profile.name || '',
          phone: profile.phone || '',
          pickupTime: 'ASAP',
          notes: '',
        });
      }
    }
  }, [profile]); // eslint-disable-line

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'paypal'>('cash');

  // ── Opening hours (webshop) — Sadik 2026-07-08 ──
  // Only ENFORCE when the branch actually has hours configured (mirrors the
  // server), so a branch without a schedule never blocks ordering.
  const [schedule, setSchedule] = useState<BranchSchedule | null>(null);
  const [hasSchedule, setHasSchedule] = useState(false);
  const [preorderValue, setPreorderValue] = useState(''); // '' = as soon as possible
  useEffect(() => {
    (async () => {
      const has = await branchHasSchedule();
      setHasSchedule(has);
      if (has) setSchedule(await loadBranchScheduleOnce());
    })();
  }, []);
  const openNow = schedule ? computeStatus(schedule).open : true;
  const preorderList: PreorderOption[] = schedule ? preorderOptions(schedule) : [];

  const formatPrice = (price: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(price);

  const isDelivery = mode === 'delivery';
  const isPickup = mode === 'pickup';

  const effectiveName = isDelivery ? deliveryInfo.name : isPickup ? pickupInfo.name : (profile?.name || '');
  const effectivePhone = isDelivery ? deliveryInfo.phone : isPickup ? pickupInfo.phone : (profile?.phone || '');
  const effectiveEmail = guestEmail || user?.email || '';

  // Is the phone a landline? If yes, email becomes mandatory (no OTP, email
  // serves as the verification/contact channel instead).
  const phoneIsMobile = isMobilePhone(effectivePhone);
  const landlineNeedsEmail = !phoneIsMobile && effectivePhone.trim().length >= 6;

  // Per-field validation helpers (used for both gating submit + red highlights)
  const fieldErrors = (() => {
    const e: Record<string, string> = {};
    if (isDelivery) {
      if (deliveryInfo.name.trim().length < 2) e.name = lang === 'de' ? 'Name ist erforderlich' : 'Name is required';
      if (deliveryInfo.street.trim().length < 3) e.street = lang === 'de' ? 'Straße ist erforderlich' : 'Street is required';
      if (deliveryInfo.postcode.trim().length < 4) e.postcode = lang === 'de' ? 'PLZ ist erforderlich' : 'Postcode is required';
      if (deliveryInfo.city.trim().length < 2) e.city = lang === 'de' ? 'Stadt ist erforderlich' : 'City is required';
      if (deliveryInfo.phone.trim().length < 6) e.phone = lang === 'de' ? 'Telefonnummer ist erforderlich' : 'Phone number is required';
      if (landlineNeedsEmail && !effectiveEmail.includes('@')) e.email = lang === 'de' ? 'E-Mail ist bei Festnetznummer erforderlich' : 'Email is required for landline numbers';
    } else if (isPickup) {
      if (pickupInfo.name.trim().length < 2) e.name = lang === 'de' ? 'Name ist erforderlich' : 'Name is required';
      if (pickupInfo.phone.trim().length < 6) e.phone = lang === 'de' ? 'Telefonnummer ist erforderlich' : 'Phone number is required';
      if (landlineNeedsEmail && !effectiveEmail.includes('@')) e.email = lang === 'de' ? 'E-Mail ist bei Festnetznummer erforderlich' : 'Email is required for landline numbers';
    } else {
      // Dine-in: require a valid table number scanned from the QR code
      const tn = parseInt(tableNumber || '0', 10);
      if (!tn || tn < 1) {
        e.table = lang === 'de'
          ? 'Bitte scannen Sie den QR-Code an Ihrem Tisch.'
          : 'Please scan the QR code on your table.';
      }
    }
    return e;
  })();

  // Recompute delivery quote whenever the customer types a new ZIP or cart subtotal changes.
  // Debounced by 300ms so every keystroke doesn't hit Firestore.
  useEffect(() => {
    if (!isDelivery) { setDeliveryQuote(null); return; }
    const zip = (deliveryInfo.postcode || '').replace(/\s+/g, '');
    if (zip.length < 4) { setDeliveryQuote(null); return; }
    let cancelled = false;
    setZoneChecking(true);
    const handle = setTimeout(async () => {
      const zone = await loadDeliveryZone(zip, currentBranchId());
      if (cancelled) return;
      setDeliveryQuote(quoteDelivery(zone, total));
      setZoneChecking(false);
    }, 300);
    return () => { cancelled = true; clearTimeout(handle); setZoneChecking(false); };
  }, [isDelivery, deliveryInfo.postcode, total]);

  // Delivery rule enforcement — zone (if defined) overrides the global fallback.
  const meetsMinimum = (() => {
    if (!isDelivery) return true;
    if (deliveryQuote) return deliveryQuote.allowed;
    return total >= MIN_DELIVERY_AMOUNT;
  })();
  const deliveryFee = isDelivery ? (deliveryQuote?.fee ?? 0) : 0;
  const grandTotal = total + deliveryFee;
  const canSubmit = items.length > 0 && meetsMinimum && Object.keys(fieldErrors).length === 0;

  // Style helper: red border + label for invalid required fields after first attempt
  const fieldStyle = (key: string): React.CSSProperties =>
    (attempted && fieldErrors[key])
      ? { borderColor: '#ef5350', boxShadow: '0 0 0 2px rgba(239,83,80,0.25)' }
      : {};

  const handleSubmit = async () => {
    setAttempted(true);
    if (!canSubmit) return;
    setError('');

    // Opening-hours gate (webshop delivery/pickup only). If closed now and no
    // pre-order time chosen, block with a friendly message. The server enforces
    // this too — this is the fast, clear client-side stop.
    if ((isDelivery || isPickup) && hasSchedule && !openNow && !preorderValue) {
      setError(lang === 'de'
        ? 'Wir haben gerade geschlossen. Bitte wählen Sie unten eine Vorbestellzeit.'
        : 'We are currently closed. Please choose a pre-order time below.');
      return;
    }

    const orderItems = items.map((item) => ({
      menu_item_id: item.menuItemId,
      name: item.name,
      name_de: item.name_de,
      price: item.price,
      quantity: item.quantity,
      category: item.categoryName,
      notes: item.notes,
      addons: item.addons.map((a) => ({ id: a.id, name: a.name, name_de: a.name_de, price: a.price })),
      // Domino's-style modifier selections carried through to POS & kitchen ticket
      selections: item.selections || null,
      modifier_snapshot: item.modifier_snapshot || null,
    }));

    const isDineIn = mode === 'dine_in';
    // Read FREECOLA-style campaign promo (set by PromoBridge from URL `?promo=`).
    let promoCode: string | null = null;
    try {
      promoCode = sessionStorage.getItem('qr_active_promo');
    } catch { /* sessionStorage blocked — silently skip */ }
    // QR session id (set by ScanTrackerBridge on first landing). Lets the admin
    // Marketing dashboard join scan → order and compute conversion %.
    let qrSessionId: string | null = null;
    let qrScanSource: 'web' | 'table' | null = null;
    try {
      qrSessionId = sessionStorage.getItem('sushikiug:qr_session_id');
      qrScanSource = isDineIn ? 'table' : 'web';
    } catch { /* sessionStorage blocked */ }
    const orderData: any = {
      items: orderItems,
      branch_id: currentBranchId(),
      // Subtotal on `total`, delivery fee tracked separately, grand_total for the order doc.
      subtotal: total,
      total: isDelivery ? grandTotal : total,
      delivery_fee: isDelivery ? deliveryFee : 0,
      source: isDineIn ? 'qr_order' : 'webshop',
      // Brand tag — POS ignores unknown fields; used by reports/routing.
      brand: brand.id,
      brand_name: brand.name,
      source_hostname: typeof window !== 'undefined' ? window.location.hostname : '',
      order_type: mode,
      payment_method: paymentMethod,
      customer_email: effectiveEmail,
      customer_uid: user?.uid || '',
      is_guest: isGuest,
      ...(promoCode ? { promo_code: promoCode } : {}),
      ...(qrSessionId ? { qr_session_id: qrSessionId } : {}),
      ...(qrScanSource ? { qr_scan_source: qrScanSource } : {}),
      // Pre-order: a chosen future slot (ISO). Server validates it's within open
      // hours and flags the order as a pre-order for the POS "Preorders" tab.
      ...(preorderValue ? { scheduled_for: preorderValue } : {}),
    };

    if (isDelivery) {
      orderData.customer_name = deliveryInfo.name;
      orderData.customer_address = `${deliveryInfo.street}, ${deliveryInfo.postcode} ${deliveryInfo.city}`;
      orderData.delivery_address = `${deliveryInfo.street}, ${deliveryInfo.postcode} ${deliveryInfo.city}`;
      orderData.customer_street = deliveryInfo.street;
      orderData.customer_phone = deliveryInfo.phone;
      orderData.customer_postcode = deliveryInfo.postcode;
      orderData.customer_city = deliveryInfo.city;
      orderData.notes = deliveryInfo.notes;
      orderData.tableNumber = 0;
      if (deliveryQuote?.zone) {
        orderData.delivery_zone = {
          postal_code: deliveryQuote.zone.postal_code,
          min_order: deliveryQuote.zone.min_order,
          delivery_fee: deliveryQuote.zone.delivery_fee,
          free_threshold: deliveryQuote.zone.free_threshold,
        };
      }
      // Best-effort geocode — gives the rider map real pins for webshop orders.
      // Failure is silent: the order still places, just without GPS coords.
      try {
        const q = encodeURIComponent(
          `${deliveryInfo.street}, ${deliveryInfo.postcode} ${deliveryInfo.city}, Germany`,
        );
        const resp = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1&countrycodes=de`,
          { headers: { 'Accept-Language': 'de' } },
        );
        if (resp.ok) {
          const arr = await resp.json();
          if (Array.isArray(arr) && arr.length > 0) {
            const lat = parseFloat(arr[0].lat);
            const lon = parseFloat(arr[0].lon);
            if (!isNaN(lat) && !isNaN(lon)) {
              orderData.delivery_latitude = lat;
              orderData.delivery_longitude = lon;
            }
          }
        }
      } catch { /* non-critical */ }
    } else if (isPickup) {
      orderData.customer_name = pickupInfo.name;
      orderData.customer_phone = pickupInfo.phone;
      orderData.notes = pickupInfo.notes;
      orderData.pickup_time = pickupInfo.pickupTime;
      orderData.tableNumber = 0;
    } else {
      orderData.tableNumber = parseInt(tableNumber || '0', 10);
    }

    // Routing: dine-in → submit directly. Mobile → OTP. Landline → submit
    // directly (email replaces OTP as the verification/contact channel).
    const isRemoteOrder = !isDineIn && (isDelivery || isPickup);
    if (isRemoteOrder && phoneIsMobile) {
      // Skip the order-OTP if the customer is already logged in via OTP with
      // the SAME phone. The login flow already verified phone ownership; forcing
      // a second OTP is what the client flagged as a bug.
      const normalizePhone = (p: string) =>
        (p || '').replace(/[\s\-()+]/g, '').replace(/^0049/, '49').replace(/^49/, '49').replace(/^0/, '49');
      try {
        const raw = localStorage.getItem('sushiki_session');
        if (raw) {
          const sess = JSON.parse(raw);
          const verifiedAt = Number(sess?.verified_at || 0);
          const maxAge = 24 * 60 * 60 * 1000; // match backend session TTL
          const phoneMatch = sess?.phone && normalizePhone(sess.phone) === normalizePhone(effectivePhone);
          if (sess?.sessionToken && verifiedAt && (Date.now() - verifiedAt) < maxAge && phoneMatch) {
            orderData.session_token = sess.sessionToken;
            setConfirmingOrder(true);
            await finalizeOrder(orderData);
            return;
          }
        }
      } catch { /* fall through to OTP modal */ }

      // No valid session — verify via SMS/WhatsApp OTP
      setPendingOrderData(orderData);
      setOtpModalOpen(true);
      return;
    }
    // Landline or dine-in: submit immediately
    if (isRemoteOrder && landlineNeedsEmail) {
      orderData.verification_method = 'landline_email';
    }
    setConfirmingOrder(true);
    await finalizeOrder(orderData);
  };

  const finalizeOrder = async (orderData: any, verificationToken?: string) => {
    setSubmitting(true);
    setError('');
    try {
      if (verificationToken) orderData.order_verification_token = verificationToken;

      // Save address to profile (logged-in, delivery only)
      if (isDelivery && user) {
        updateDoc(doc(db, 'customers', user.uid), {
          street: deliveryInfo.street.trim(),
          postcode: deliveryInfo.postcode.trim(),
          city: deliveryInfo.city.trim(),
          phone: deliveryInfo.phone.trim(),
          name: deliveryInfo.name.trim(),
          updated_at: serverTimestamp(),
        }).catch(() => {});
      }

      // Same cart ⇒ same id, so a retry after a timeout can't double-order.
      orderData.client_request_id = getOrderRequestId(orderData);
      const result = await submitQrOrder(orderData);
      const data = result.data as any;
      const orderNumber = data.order_number || data.orderNumber || '';
      const orderId = data.order_id || '';

      clearCart();
      clearOrderRequestId();
      // Promo is single-use per session — clear after the order it applied to is placed.
      try { sessionStorage.removeItem('qr_active_promo'); } catch {}
      try { localStorage.setItem(`order_${orderNumber}`, orderId); } catch {}

      // PayPal: create PayPal order and redirect customer to approve payment
      if (PAYPAL_ENABLED && paymentMethod === 'paypal' && orderId) {
        const paypalResult = await paypalCreateOrder({ amount: total, orderId, currency: 'EUR' }) as any;
        const pd = paypalResult.data as any;
        if (pd?.success && pd?.approvalUrl) {
          try {
            sessionStorage.setItem('paypal_pending', JSON.stringify({ orderId, orderNumber }));
          } catch { /* ignore */ }
          window.location.href = pd.approvalUrl;
          return; // navigation handled by PayPal redirect
        }
        throw new Error(pd?.error || 'PayPal order creation failed');
      }

      navigate(`/confirmed/${orderNumber}`, {
        state: { orderId, isGuest, guestEmail: effectiveEmail, guestName: effectiveName, guestPhone: effectivePhone },
      });
    } catch (err: any) {
      console.error('Order submission error:', err);
      const detail = err?.details?.message || err?.details || err?.message || '';
      setError(detail ? `${t('submitError')} (${detail})` : t('submitError'));
      setConfirmingOrder(false);
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Auto-submit for logged-in customers with a complete profile ──
  // Client requirement: once the customer has verified their phone via OTP at
  // login AND saved their address/profile, do NOT ask them to fill the
  // checkout form again. Clicking "Go to Checkout" from the cart becomes
  // equivalent to "Place Order".
  //
  // Trigger conditions:
  //   • User is authenticated (`user` exists)
  //   • Profile loaded with all required fields for the selected order type
  //   • Delivery: zone quote resolved AND cart meets the minimum
  //   • Not a guest, not dine-in (table flow is different)
  //   • `canSubmit` is true (validation passes)
  //   • Not already submitting / in OTP flow
  const autoSubmittedRef = useRef(false);
  useEffect(() => {
    if (autoSubmittedRef.current) return;
    if (!user) return;                          // guests still see the form
    if (mode !== 'delivery' && mode !== 'pickup') return; // dine-in flow unchanged
    if (items.length === 0) return;
    if (submitting || confirmingOrder || otpModalOpen) return;
    if (!canSubmit) return;                     // form has missing fields → show it
    if (mode === 'delivery' && zoneChecking) return; // wait for live fee/min check
    if (mode === 'delivery' && !deliveryQuote) return;

    autoSubmittedRef.current = true;
    setConfirmingOrder(true);   // show overlay — customer never sees the form
    handleSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, profile, mode, items.length, canSubmit, zoneChecking, deliveryQuote, submitting, confirmingOrder, otpModalOpen]);

  // ─── Render ──────────────────────────────────────────────────

  // Full-screen overlay while order is being submitted after OTP / landline skip
  if (confirmingOrder) {
    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 20000,
        background: '#111322', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 16,
      }}>
        <div style={{
          width: 56, height: 56, border: '4px solid var(--brand-primary)', borderTopColor: 'transparent',
          borderRadius: '50%', animation: 'spin 0.8s linear infinite',
        }} />
        <p style={{ color: 'var(--brand-primary)', fontSize: '1.1rem', fontWeight: 700 }}>
          {lang === 'de' ? 'Bestellung wird aufgegeben...' : 'Placing your order...'}
        </p>
        <p style={{ color: '#9ca3af', fontSize: '0.85rem' }}>
          {lang === 'de' ? 'Bitte warten Sie einen Moment' : 'Please wait a moment'}
        </p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const rootClass = inModal ? 'checkout-page checkout-page--modal' : 'page checkout-page';
  const handleBack = () => {
    if (inModal && onClose) onClose();
    else navigate(-1);
  };

  if (items.length === 0) {
    return (
      <div className={rootClass}>
        <header className="menu-header">
          <button className="back-btn" onClick={handleBack}>&#8592;</button>
          <div className="header-info">
            <span className="header-brand">{t('checkout')}</span>
          </div>
          {inModal && (
            <button className="back-btn" onClick={handleBack} aria-label="Close" style={{ marginLeft: 'auto' }}>&times;</button>
          )}
        </header>
        <div className="empty-cart">
          <p>{t('emptyCart')}</p>
          <button className="btn-primary" onClick={() => inModal && onClose ? onClose() : navigate('/')}>{t('backToMenu')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className={rootClass}>
      <header className="menu-header">
        <button className="back-btn" onClick={handleBack}>&#8592;</button>
        <div className="header-info">
          <span className="header-brand">{t('checkout')}</span>
          <span className="header-table">
            {isDelivery ? t('delivery') : isPickup ? t('pickup') : `${t('table')} ${tableNumber}`}
          </span>
        </div>
        <div className="lang-toggle" style={{ position: 'relative', top: 'auto', right: 'auto', marginLeft: 'auto' }}>
          <button className={lang === 'de' ? 'active' : ''} onClick={() => setLang('de')}>DE</button>
          <button className={lang === 'en' ? 'active' : ''} onClick={() => setLang('en')}>EN</button>
        </div>
        {inModal && (
          <button className="back-btn" onClick={handleBack} aria-label="Close" style={{ marginLeft: 8, fontSize: '1.6rem' }}>&times;</button>
        )}
      </header>

      <div className="checkout-grid">
        <div className="checkout-form-col">

      {/* Opening hours + pre-order (webshop only, when the branch has hours set) */}
      {hasSchedule && (isDelivery || isPickup) && (
        <div style={{
          margin: '0 16px 12px', padding: '12px 16px', borderRadius: 10,
          background: openNow ? 'rgba(76,175,80,0.08)' : 'rgba(239,83,80,0.10)',
          border: `1px solid ${openNow ? 'rgba(76,175,80,0.25)' : 'rgba(239,83,80,0.35)'}`,
        }}>
          <div style={{ fontWeight: 700, color: openNow ? '#8bc34a' : '#ef9a9a', fontSize: '0.9rem' }}>
            {openNow
              ? (lang === 'de' ? 'Jetzt geöffnet' : 'Open now')
              : (lang === 'de' ? 'Wir haben gerade geschlossen' : 'We are currently closed')}
          </div>
          <div style={{ fontSize: '0.78rem', color: '#9ca3af', marginTop: 2, marginBottom: preorderList.length ? 8 : 0 }}>
            {openNow
              ? (lang === 'de' ? 'Sie können sofort bestellen — oder für später vorbestellen.' : 'Order now — or pre-order for later.')
              : (lang === 'de' ? 'Bitte wählen Sie eine Vorbestellzeit innerhalb unserer Öffnungszeiten.' : 'Please choose a pre-order time within our opening hours.')}
          </div>
          {preorderList.length > 0 && (
            <select
              value={preorderValue}
              onChange={(e) => setPreorderValue(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px', borderRadius: 8,
                background: 'rgba(255,255,255,0.05)', color: '#e0e0e0',
                border: preorderValue ? '2px solid var(--brand-primary)' : '1px solid #3a3d52', fontSize: '0.9rem',
              }}
            >
              {openNow && <option value="">{lang === 'de' ? 'So bald wie möglich' : 'As soon as possible'}</option>}
              {!openNow && <option value="" disabled>{lang === 'de' ? 'Vorbestellzeit wählen…' : 'Choose a pre-order time…'}</option>}
              {preorderList.map((o) => (
                <option key={o.value} value={o.value}>
                  {new Date(o.value).toLocaleDateString(lang === 'de' ? 'de-DE' : 'en-GB', { weekday: 'short', day: '2-digit', month: '2-digit' })} · {o.time}
                </option>
              ))}
            </select>
          )}
          {!openNow && preorderList.length === 0 && (
            <div style={{ fontSize: '0.78rem', color: '#ef9a9a' }}>
              {lang === 'de' ? 'Zurzeit keine Vorbestelltermine verfügbar.' : 'No pre-order times available right now.'}
            </div>
          )}
        </div>
      )}

      {/* Guest info banner */}
      {isGuest && (isDelivery || isPickup) && (
        <div style={{
          margin: '0 16px 12px', padding: '12px 16px',
          background: 'rgba(76,175,80,0.08)', border: '1px solid rgba(76,175,80,0.2)',
          borderRadius: 10,
        }}>
          <div style={{ fontWeight: 600, color: '#e0e0e0', fontSize: '0.85rem' }}>
            {lang === 'de' ? 'Ihre Kontaktdaten' : 'Your contact details'}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: 2 }}>
            {lang === 'de'
              ? 'Bitte geben Sie Ihre Daten ein, damit wir Sie kontaktieren können.'
              : 'Please enter your details so we can contact you about your order.'}
          </div>
        </div>
      )}

      {/* Order summary */}
      <div className="checkout-section">
        <h3 className="section-title">{t('yourOrder')} ({itemCount} {t('items')})</h3>
        <div className="checkout-items">
          {items.map((item) => {
            const addonTotal = item.addons.reduce((s, a) => s + a.price, 0);
            const lineTotal = (item.price + addonTotal) * item.quantity;
            return (
              <div key={item.cartId} className="checkout-item-row">
                <span className="checkout-item-qty">{item.quantity}x</span>
                <span className="checkout-item-name">
                  {item.name_de || item.name}
                  {item.addons.length > 0 && (
                    <span className="checkout-item-addons"> + {item.addons.map(a => a.name_de || a.name).join(', ')}</span>
                  )}
                </span>
                <span className="checkout-item-price">{formatPrice(lineTotal)}</span>
              </div>
            );
          })}
        </div>
        <button className="edit-cart-btn" onClick={() => navigate('/cart')}>
          {t('editCart')}
        </button>
      </div>

      {/* Delivery form */}
      {isDelivery && (
        <div className="checkout-section">
          <h3 className="section-title">📍 {t('deliveryAddress')}</h3>
          {/* Zone-aware warning messages (take priority over the global fallback) */}
          {deliveryQuote && !deliveryQuote.allowed && deliveryQuote.reason === 'no_zone' && (
            <p className="min-order-warning">
              {lang === 'de'
                ? `❌ Lieferung in PLZ ${deliveryInfo.postcode} derzeit nicht möglich.`
                : `❌ Delivery to ${deliveryInfo.postcode} is not available.`}
            </p>
          )}
          {deliveryQuote && !deliveryQuote.allowed && deliveryQuote.reason === 'disabled' && (
            <p className="min-order-warning">
              {lang === 'de'
                ? `⏸️ Lieferung in PLZ ${deliveryInfo.postcode} vorübergehend pausiert.`
                : `⏸️ Delivery to ${deliveryInfo.postcode} is temporarily paused.`}
            </p>
          )}
          {deliveryQuote && !deliveryQuote.allowed && deliveryQuote.reason === 'below_min' && deliveryQuote.zone && (
            <p className="min-order-warning">
              {lang === 'de'
                ? `Mindestbestellwert für PLZ ${deliveryQuote.zone.postal_code}: ${formatPrice(deliveryQuote.zone.min_order)}. Es fehlen noch ${formatPrice(deliveryQuote.amountBelowMin)}.`
                : `Minimum order for ${deliveryQuote.zone.postal_code}: ${formatPrice(deliveryQuote.zone.min_order)}. Add ${formatPrice(deliveryQuote.amountBelowMin)} more.`}
            </p>
          )}
          {/* Fallback: no zone configured AND below global minimum */}
          {!deliveryQuote && !meetsMinimum && (
            <p className="min-order-warning">
              {t('minOrder')}: {formatPrice(MIN_DELIVERY_AMOUNT)}
            </p>
          )}
          {/* "Spend €X more for free delivery" hint */}
          {deliveryQuote && deliveryQuote.allowed && !deliveryQuote.isFree && deliveryQuote.amountToFreeDelivery > 0 && (
            <p style={{ margin: '0 0 12px', padding: '8px 12px', borderRadius: 8, background: 'rgba(148,232,21,0.08)', color: 'var(--brand-primary)', fontSize: '0.82rem', fontWeight: 600 }}>
              {lang === 'de'
                ? `💡 Noch ${formatPrice(deliveryQuote.amountToFreeDelivery)} bis zur kostenlosen Lieferung.`
                : `💡 Add ${formatPrice(deliveryQuote.amountToFreeDelivery)} more for free delivery.`}
            </p>
          )}
          {deliveryQuote && deliveryQuote.allowed && deliveryQuote.isFree && (
            <p style={{ margin: '0 0 12px', padding: '8px 12px', borderRadius: 8, background: 'rgba(148,232,21,0.15)', color: 'var(--brand-primary)', fontSize: '0.85rem', fontWeight: 700 }}>
              {lang === 'de' ? '🎉 Gratis Lieferung!' : '🎉 Free delivery!'}
            </p>
          )}
          {zoneChecking && (
            <p style={{ margin: '0 0 8px', color: '#9ca3af', fontSize: '0.75rem' }}>
              {lang === 'de' ? 'PLZ wird geprüft…' : 'Checking postal code…'}
            </p>
          )}
          <div className="form-grid">
            <div className="form-field full">
              <label>{t('fullName')} <span style={{ color: '#ef5350' }}>*</span></label>
              <input type="text" value={deliveryInfo.name} placeholder="Max Mustermann" style={fieldStyle('name')}
                onChange={(e) => setDeliveryInfo({ ...deliveryInfo, name: e.target.value })} />
              {attempted && fieldErrors.name && <span className="field-error">{fieldErrors.name}</span>}
            </div>
            <div className="form-field full">
              <label>{t('street')} <span style={{ color: '#ef5350' }}>*</span></label>
              <input type="text" value={deliveryInfo.street} placeholder="Musterstraße 12" style={fieldStyle('street')}
                onChange={(e) => setDeliveryInfo({ ...deliveryInfo, street: e.target.value })} />
              {attempted && fieldErrors.street && <span className="field-error">{fieldErrors.street}</span>}
            </div>
            <div className="form-field">
              <label>{t('postcode')} <span style={{ color: '#ef5350' }}>*</span></label>
              <input type="text" value={deliveryInfo.postcode} placeholder="50354" style={fieldStyle('postcode')}
                onChange={(e) => setDeliveryInfo({ ...deliveryInfo, postcode: e.target.value })} />
              {attempted && fieldErrors.postcode && <span className="field-error">{fieldErrors.postcode}</span>}
            </div>
            <div className="form-field">
              <label>{t('city')} <span style={{ color: '#ef5350' }}>*</span></label>
              <input type="text" value={deliveryInfo.city} placeholder="Hürth" style={fieldStyle('city')}
                onChange={(e) => setDeliveryInfo({ ...deliveryInfo, city: e.target.value })} />
              {attempted && fieldErrors.city && <span className="field-error">{fieldErrors.city}</span>}
            </div>
            <div className="form-field full">
              <label>{t('phone')} <span style={{ color: '#ef5350' }}>*</span></label>
              <PhoneInput
                international defaultCountry="DE"
                value={deliveryInfo.phone}
                onChange={(v) => setDeliveryInfo({ ...deliveryInfo, phone: v || '' })}
                placeholder="176 12345678"
                className={attempted && fieldErrors.phone ? 'phone-input-error' : ''}
              />
              {attempted && fieldErrors.phone && <span className="field-error">{fieldErrors.phone}</span>}
              {landlineNeedsEmail && (
                <span style={{ fontSize: '0.72rem', color: '#FFC107', marginTop: 4, display: 'block' }}>
                  {lang === 'de'
                    ? '📞 Festnetznummer erkannt — bitte geben Sie unten Ihre E-Mail ein.'
                    : '📞 Landline detected — please enter your email below.'}
                </span>
              )}
            </div>
            {/* Email — optional for mobile, REQUIRED for landline */}
            <div className="form-field full">
              <label>
                E-Mail {landlineNeedsEmail
                  ? <span style={{ color: '#ef5350' }}>*</span>
                  : <span style={{ color: '#9ca3af', fontSize: '0.78rem' }}>({lang === 'de' ? 'optional' : 'optional'})</span>}
              </label>
              <input type="email" value={guestEmail} placeholder="name@example.com" style={fieldStyle('email')}
                onChange={(e) => setGuestEmail(e.target.value)} />
              {attempted && fieldErrors.email && <span className="field-error">{fieldErrors.email}</span>}
            </div>
            <div className="form-field full">
              <label>{t('deliveryNotes')}</label>
              <textarea value={deliveryInfo.notes} rows={2}
                placeholder={t('deliveryNotesPlaceholder')}
                onChange={(e) => setDeliveryInfo({ ...deliveryInfo, notes: e.target.value })} />
            </div>
          </div>
        </div>
      )}

      {/* Pickup form */}
      {isPickup && (
        <div className="checkout-section">
          <h3 className="section-title">🏃 {t('pickupDetails')}</h3>
          <div className="form-grid">
            <div className="form-field full">
              <label>{t('fullName')} <span style={{ color: '#ef5350' }}>*</span></label>
              <input type="text" value={pickupInfo.name} placeholder="Max Mustermann" style={fieldStyle('name')}
                onChange={(e) => setPickupInfo({ ...pickupInfo, name: e.target.value })} />
              {attempted && fieldErrors.name && <span className="field-error">{fieldErrors.name}</span>}
            </div>
            <div className="form-field full">
              <label>{t('phone')} <span style={{ color: '#ef5350' }}>*</span></label>
              <PhoneInput
                international defaultCountry="DE"
                value={pickupInfo.phone}
                onChange={(v) => setPickupInfo({ ...pickupInfo, phone: v || '' })}
                placeholder="176 12345678"
                className={attempted && fieldErrors.phone ? 'phone-input-error' : ''}
              />
              {attempted && fieldErrors.phone && <span className="field-error">{fieldErrors.phone}</span>}
              {landlineNeedsEmail && (
                <span style={{ fontSize: '0.72rem', color: '#FFC107', marginTop: 4, display: 'block' }}>
                  {lang === 'de'
                    ? '📞 Festnetznummer erkannt — bitte geben Sie unten Ihre E-Mail ein.'
                    : '📞 Landline detected — please enter your email below.'}
                </span>
              )}
            </div>
            {/* Email — optional for mobile, REQUIRED for landline */}
            <div className="form-field full">
              <label>
                E-Mail {landlineNeedsEmail
                  ? <span style={{ color: '#ef5350' }}>*</span>
                  : <span style={{ color: '#9ca3af', fontSize: '0.78rem' }}>({lang === 'de' ? 'optional' : 'optional'})</span>}
              </label>
              <input type="email" value={guestEmail} placeholder="name@example.com" style={fieldStyle('email')}
                onChange={(e) => setGuestEmail(e.target.value)} />
              {attempted && fieldErrors.email && <span className="field-error">{fieldErrors.email}</span>}
            </div>
            <div className="form-field full">
              <label>{t('pickupTime')}</label>
              <div style={{ display: 'flex', gap: 10, flexDirection: 'column' }}>
                <button type="button" onClick={() => setPickupInfo({ ...pickupInfo, pickupTime: 'ASAP' })}
                  style={{
                    padding: '14px 18px', borderRadius: 12,
                    background: pickupInfo.pickupTime === 'ASAP' ? 'var(--brand-primary)' : 'rgba(255,255,255,0.04)',
                    color: pickupInfo.pickupTime === 'ASAP' ? '#1a1d2e' : '#e0e0e0',
                    border: pickupInfo.pickupTime === 'ASAP' ? '2px solid var(--brand-primary)' : '1px solid #3a3d52',
                    fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    transition: 'all 0.2s',
                  }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: '1.3rem' }}>⚡</span>
                    <span>{t('asap')}</span>
                  </span>
                  <span style={{ fontSize: '0.78rem', opacity: 0.85 }}>~15-25 min</span>
                </button>
                <button type="button" onClick={() => setTimePickerOpen(true)}
                  style={{
                    padding: '14px 18px', borderRadius: 12,
                    background: pickupInfo.pickupTime !== 'ASAP' ? 'var(--brand-primary)' : 'rgba(255,255,255,0.04)',
                    color: pickupInfo.pickupTime !== 'ASAP' ? '#1a1d2e' : '#e0e0e0',
                    border: pickupInfo.pickupTime !== 'ASAP' ? '2px solid var(--brand-primary)' : '1px solid #3a3d52',
                    fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    transition: 'all 0.2s',
                  }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: '1.3rem' }}>🕐</span>
                    <span>{lang === 'de' ? 'Bestimmte Uhrzeit' : 'Specific time'}</span>
                  </span>
                  <span style={{
                    fontSize: '1.05rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                    background: pickupInfo.pickupTime !== 'ASAP' ? 'rgba(26,29,46,0.15)' : 'rgba(148,232,21,0.1)',
                    color: pickupInfo.pickupTime !== 'ASAP' ? '#1a1d2e' : 'var(--brand-primary)',
                    padding: '4px 10px', borderRadius: 8,
                  }}>
                    {pickupInfo.pickupTime !== 'ASAP' ? pickupInfo.pickupTime : '--:--'}
                  </span>
                </button>
              </div>
              {timePickerOpen && (
                <AnalogTimePicker
                  value={pickupInfo.pickupTime !== 'ASAP' ? pickupInfo.pickupTime : ''}
                  onChange={(t) => setPickupInfo({ ...pickupInfo, pickupTime: t })}
                  onClose={() => setTimePickerOpen(false)}
                  lang={lang === 'de' ? 'de' : 'en'}
                />
              )}
            </div>
            <div className="form-field full">
              <label>{t('notes')}</label>
              <textarea value={pickupInfo.notes} rows={2}
                placeholder={t('notesPlaceholder')}
                onChange={(e) => setPickupInfo({ ...pickupInfo, notes: e.target.value })} />
            </div>
          </div>
        </div>
      )}

      {/* Payment method */}
      <div className="checkout-section">
        <h3 className="section-title">💳 {t('paymentMethod')}</h3>
        <div className="payment-options">
          <button className={`payment-btn ${paymentMethod === 'cash' ? 'active' : ''}`}
            onClick={() => setPaymentMethod('cash')}>
            <span>💵</span> {isDelivery ? t('cashOnDelivery') : t('cashOnPickup')}
          </button>
          {PAYPAL_ENABLED && (
            <button className={`payment-btn ${paymentMethod === 'paypal' ? 'active' : ''}`}
              onClick={() => setPaymentMethod('paypal')}>
              <span>💳</span> PayPal
            </button>
          )}
        </div>
      </div>

        </div>
        <div className="checkout-summary-col">

      {/* Total & submit */}
      <div className="checkout-total">
        <div className="total-row">
          <span>{t('subtotal')}</span>
          <span>{formatPrice(total)}</span>
        </div>
        {isDelivery && (
          <div className="total-row delivery-fee">
            <span>{t('deliveryFee')}</span>
            {deliveryQuote && deliveryQuote.isFree
              ? <span className="free-tag">{t('free')}</span>
              : <span>{formatPrice(deliveryFee)}</span>}
          </div>
        )}
        <div className="total-row final">
          <span>{t('total')}</span>
          <span className="total-price">{formatPrice(isDelivery ? grandTotal : total)}</span>
        </div>
      </div>

      {/* Validation summary — shown after first submit attempt if fields are still missing */}
      {attempted && Object.keys(fieldErrors).length > 0 && (
        <div style={{
          margin: '0 16px 12px', padding: '12px 16px',
          background: 'rgba(239,83,80,0.08)', border: '1px solid rgba(239,83,80,0.3)',
          borderRadius: 10, color: '#ef5350', fontSize: '0.82rem',
        }}>
          {lang === 'de'
            ? `Bitte füllen Sie alle Pflichtfelder aus (${Object.keys(fieldErrors).length} fehlen)`
            : `Please fill in all required fields (${Object.keys(fieldErrors).length} missing)`}
        </div>
      )}

      {error && <p className="error-text">{error}</p>}

      <button className="btn-primary submit-btn" onClick={handleSubmit} disabled={submitting}>
        {submitting
          ? t('submitting')
          : phoneIsMobile || !effectivePhone.trim()
            ? `${t('placeOrder')} — ${formatPrice(isDelivery ? grandTotal : total)}`
            : `${lang === 'de' ? 'Jetzt bestellen' : 'Order Now'} — ${formatPrice(isDelivery ? grandTotal : total)}`}
      </button>

        </div>
      </div>

      {/* Order OTP Verification Modal — only for mobile numbers */}
      <OrderOTPModal
        open={otpModalOpen}
        phone={effectivePhone}
        amount={isDelivery ? grandTotal : total}
        onVerified={(token) => {
          setOtpModalOpen(false);
          // Show "Placing order..." overlay IMMEDIATELY so the customer doesn't
          // see the checkout form flash behind before navigation to confirmation.
          setConfirmingOrder(true);
          if (pendingOrderData) {
            finalizeOrder(pendingOrderData, token);
            setPendingOrderData(null);
          }
        }}
        onClose={() => {
          setOtpModalOpen(false);
          setPendingOrderData(null);
          setSubmitting(false);
        }}
        lang={lang === 'de' ? 'de' : 'en'}
      />
    </div>
  );
};

export default CheckoutPage;
